import React, { useContext, useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { format } from "date-fns";
import { useMediaQuery } from "@mui/material";
import { ColorModeContext } from "../../theme/ColorModeContext";
import { chartAxisStyle, getChartTheme } from "./chartTheme";

const HeightChart = (Props: any) => {
  const {
    motherHeight,
    fatherHeight,
    heightList,
    heightChartZoneData,
    isMobileScreen,
    isDownload
  } = Props;
  const { mode } = useContext(ColorModeContext);
  const chartTheme = useMemo(
    () => getChartTheme(mode, Boolean(isDownload)),
    [mode, isDownload],
  );

  const {
    currentHeight,
    expectedHeight,
    zoneCenterLine,
    yAxisMaxPoint,
    maxDate,
    lastZone,
    lastZoneTime,
    mh,
    fh,
    lastTimestamp
  } = useMemo(() => {
    const mh = motherHeight ?? 0;
    const fh = fatherHeight ?? 0;

    const filteredHeights = heightList
      ?.filter((item: any) => item?.height && item?.height > 0)
      ?.map((item: any) => [new Date(item?.chartLabel).getTime(), item?.height]);

    const expectedHeight = heightList
      ?.filter((x: any) => {
        const isMoreThanOneYear =
          x?.age?.includes("y") && parseInt(x?.age?.split("y")[0]) >= 1;
        return isMoreThanOneYear && x?.chartExpectedHeight > 0;
      })
      ?.map((item: any) => [
        new Date(item?.chartLabel).getTime(),
        item?.chartExpectedHeight,
      ]);

    const maxDate = Math.max(...filteredHeights.map((d: any) => d[0]));
    const lastZone = heightChartZoneData[heightChartZoneData.length - 1];
    const lastZoneTime = new Date(lastZone?.ageDate).getTime();

    const zoneCenterLine = heightChartZoneData
      .filter((h: any) => new Date(h?.ageDate).getTime() <= maxDate)
      .map((h: any) => [new Date(h?.ageDate).getTime(), h.zoneCenterGreenValue]);

    if (lastZoneTime < maxDate) {
      zoneCenterLine.push([maxDate, lastZone.zoneCenterGreenValue]);
    }

    const maxHightZoneValue = Math.max(
      heightChartZoneData?.reduce(
        (curr: number, item: any) => Math.max(curr, item.zoneUpperOrangeMaxValue ?? 0),
        0
      ) ?? 0,
      mh,
      fh
    );

    const maxHeight = filteredHeights?.reduce(
      (curr: number, item: any) => Math.max(curr, item[1] ?? 0),
      0
    ) || 0;

    const yAxisMaxPoint = Math.ceil(Math.max(maxHightZoneValue, maxHeight) / 10) * 10;

    const lastTimestamp = new Date(
      heightList[heightList.length - 1]?.chartLabel
    ).getTime();
    
    return {
      currentHeight: filteredHeights,
      expectedHeight,
      zoneCenterLine,
      yAxisMaxPoint,
      maxDate,
      lastZone,
      lastZoneTime,
      mh,
      fh,
      lastTimestamp
    };
  }, [heightList, heightChartZoneData, motherHeight, fatherHeight]);

  // ✅ Hide chart if no data
  const hasData =
    (currentHeight?.length ?? 0) > 0 ||
    (expectedHeight?.length ?? 0) > 0 ||
    mh > 0 ||
    fh > 0;

  if (!hasData) {
    return null;
  }

  // Convert zone data to polygon format
  const createZonePolygon = (
    name: string,
    minKey: string,
    maxKey: string,
    color: string
  ) => {
    const maxPoints = heightChartZoneData.map((p: any) => [
      new Date(p?.ageDate).getTime(),
      p[maxKey],
    ]);
    const minPoints = heightChartZoneData
      .map((p: any) => [new Date(p?.ageDate).getTime(), p[minKey]])
      .reverse();

    if (lastZoneTime < maxDate) {
      maxPoints.push([maxDate, lastZone[maxKey]]);
      minPoints.unshift([maxDate, lastZone[minKey]]);
    }

    const polygonPoints = maxPoints.concat(minPoints);

    return {
      name,
      type: "line",
      data: polygonPoints,
      lineStyle: { width: 0 },
      symbol: "none",
      areaStyle: { color, opacity: 0.3 },
      smooth: true,
      tooltip: { show: false },
      z: 1,
      animation: false, // Disable animation
    };
  };

  const color = {
    red: "#FF0000",
    orange: "#FFA500",
    yellow: "#FFFF00",
    green: "#008000",
  };

  const HeightOption = {
    animation: false, //  Disable global animation
    animationDuration: 0,
    animationEasing: 'linear',
    animationThreshold: 0,
    progressive: 0,
    hoverLayerThreshold: 0,
    backgroundColor: "transparent",
    textStyle: { color: chartTheme.text },
    title: {
      text: "Height Chart",
      left: "center",
      top: isMobileScreen ? 5 : 10, // small offset
      textStyle: {
        fontSize: isMobileScreen ? 13 : 18,
        fontWeight: 600,
        color: chartTheme.text,
      },
    },
    tooltip: {
      trigger: "axis",
      backgroundColor: chartTheme.tooltipBg,
      borderColor: chartTheme.tooltipBorder,
      borderWidth: 1,
      textStyle: {
        color: chartTheme.tooltipText,
        fontSize: 12,
      },
      formatter: function (params: any) {
        const dotColors = {
          "Current Height": chartTheme.currentLine,
          "Expected Height": chartTheme.currentLine,
          "Mother's Height": "#c084fc",
          "Father's Height": "#60a5fa",
        };

        const firstPoint = params[0].value[0];
        const seriesData = heightList.find(
          (x: any) => new Date(x.chartLabel).getTime() === firstPoint
        );

        const age = seriesData ? seriesData.age : null;

        let html = `<div style="text-align:left;color:${chartTheme.tooltipText};">`;
        html += `<div style="font-weight:bold; margin-bottom:4px;">
          ${format(new Date(params[0].value[0]), "dd-MM-yyyy")}
          ${age ? `(${age})` : ""}
        </div>`;

        params.forEach((item: any) => {
          const c = dotColors[item.seriesName] || item.color;
          html += `<div style="margin-bottom:2px;">
            <span style="width:8px; height:8px; border-radius:50%; background-color:${c}; display:inline-block; margin-right:6px;"></span>
            <span>${item.seriesName}: 
              <strong>
                ${item.data[1]?.toFixed(2)}
              </strong> cm
            </span>
          </div>`;
        });

        html += `</div>`;
        return html;
      },
    },
    legend: {
      data: [
        { name: "91-99 Percentile",itemStyle: { color: "#FFA500" } },
        {name: "76-90 Percentile",itemStyle: { color: "#FFFF00" }  },
        { name: "50 Percentile", itemStyle: { color: "#008000" } },
        { name: "26-75 Percentile", itemStyle: { color: "#008000" } },
        { name: "Current Height", itemStyle: { color: chartTheme.currentLine } },
        { name: "11-25 Percentile", itemStyle: { color: "#FFFF00" } },
        { name: "4-10 Percentile", itemStyle: { color: "#FFA500" } },
        { name: "1-3 Percentile", itemStyle: { color: "#FF0000" } },
        ...(expectedHeight.length > 0
          ? [{ name: "Expected Height", itemStyle: { color: chartTheme.currentLine } }
          ]
          : []),
        { name: "Mother's Height", itemStyle: { color: "#c084fc" } },
        { name: "Father's Height", itemStyle: { color: "#60a5fa" } },
      ],
      type: "plain",
      orient: isMobileScreen ? "horizontal" : "vertical",
      top: isMobileScreen ? 25 : "middle",     // ✅ move legend below title
      left: isMobileScreen ? "center" : "auto",
      right: isMobileScreen ? "auto" : 10,
      bottom: isMobileScreen ? "auto" : "auto",
      align: isMobileScreen ? "auto" : "left",
      itemWidth: 14,
      itemHeight: 10,
      textStyle: {
        fontSize: isMobileScreen ? 10 : 11,
        color: chartTheme.text,
      },
      pageTextStyle: { color: chartTheme.text },
      padding: isMobileScreen ? [5, 5, 5, 5] : 0, // ✅ more breathing room
    },
    grid: {
      top: isMobileScreen ? 130 : 50,
      right: isMobileScreen ? 0 : 130,
      left: isMobileScreen ? 60 : 100,
    },
    xAxis: {
      type: "time",
      name: "Age",
      nameLocation: "middle",
      nameGap: 35,
      ...chartAxisStyle(chartTheme),
    },
    yAxis: {
      type: "value",
      min: 0,
      max: yAxisMaxPoint,
      name: "Height (cm)",
      nameLocation: "middle",
      nameRotate: 90,
      interval: Math.ceil(yAxisMaxPoint / 20),
      nameGap: 40,
      ...chartAxisStyle(chartTheme),
    },
    // dataZoom: [
    //   {
    //     type: "inside",
    //     start: 0,
    //     end: 100,
    //   },
    //   {
    //     start: 0,
    //     end: 100,
    //   },
    // ],
    dataZoom: isDownload
      ? [] // hide sliders when downloading
      : [
        // Horizontal zoom (x-axis)
        {
          type: 'inside',
          xAxisIndex: 0,
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
        },
        {
          type: 'slider',
          xAxisIndex: 0,
          start: 0,
          end: 100,
          bottom: isMobileScreen ? 10 : 0, // ✅ slightly above bottom for mobile
          handleSize: 8,
          handleStyle: { borderWidth: 1 },
          borderWidth: 1,
          height: 12, // thinner horizontal slider
        },
        // Vertical zoom (y-axis)
        {
          type: 'inside',
          yAxisIndex: 0,
          zoomOnMouseWheel: true,
          moveOnMouseMove: true,
        },
        // {
        //   type: 'slider',
        //   yAxisIndex: 0,
        //   start: 0,
        //   end: 100,
        //   left: isMobileScreen ? 40 : 80, // ✅ push closer on mobile
        //   orient: 'vertical',
        //   width: 8, // ✅ thinner vertical line
        //   handleSize: 8,
        //   handleStyle: { borderWidth: 1 },
        //   borderWidth: 1,
        // },
      ],
    series: [
      // Zones as polygons
      createZonePolygon(
        "1-3 Percentile",
        "zoneLowerRedMinValue",
        "zoneLowerRedMaxValue",
        color.red
      ),
      createZonePolygon(
        "4-10 Percentile",
        "zoneLowerOrangeMinValue",
        "zoneLowerOrangeMaxValue",
        color.orange
      ),
      createZonePolygon(
        "11-25 Percentile",
        "zoneLowerYellowMinValue",
        "zoneLowerYellowMaxValue",
        color.yellow
      ),
      createZonePolygon(
        "26-75 Percentile",
        "zoneLowerGreenMinValue",
        "zoneLowerGreenMaxValue",
        color.green
      ),
      createZonePolygon(
        "26-75 Percentile",
        "zoneUpperGreenMinValue",
        "zoneUpperGreenMaxValue",
        color.green
      ),
      createZonePolygon(
        "76-90 Percentile",
        "zoneUpperYellowMinValue",
        "zoneUpperYellowMaxValue",
        color.yellow
      ),
      createZonePolygon(
        "91-99 Percentile",
        "zoneUpperOrangeMinValue",
        "zoneUpperOrangeMaxValue",
        color.orange
      ),
      createZonePolygon(
        "",
        "zoneUpperRedMinValue",
        "zoneUpperRedMaxValue",
        color.red
      ),

      // Center green dashed line
      {
        name: "50 Percentile",
        type: "line",
        // data: heightChartZoneData.map((h) => [
        //   new Date(h.ageDate).getTime(),
        //   h.zoneCenterGreenValue,
        // ]),
        data: zoneCenterLine,
        lineStyle: {
          color: color.green,
          width: 1,
          type: "solid"
        },
        showSymbol: false,
        tooltip: { show: false },
        z: 3,
        animation: false,//  Disable line animation
      },

      // Current Height
      {
        name: "Current Height",
        type: "line",
        data: currentHeight,
        lineStyle: {
          type: "solid",
          width: 2,
          color: chartTheme.currentLine,
        },
        symbol: "circle",
        itemStyle: {
          color: chartTheme.currentLine,
        },
        symbolSize: 6,
        z: 4,
        smooth: true,
        animation: false,//  Disable line animation
      },

      // Expected height
      {
        name: "Expected Height",
        type: "line",
        data: expectedHeight,
        lineStyle: {
          type: "dashed",
          width: 2,
          color: chartTheme.currentLine,
        },
        symbol: "circle",
        itemStyle: {
          color: chartTheme.currentLine,
        },
        symbolSize: 6,
        z: 4,
        smooth: true,
        animation: false,//  Disable line animation
      },

      // Mother's Height
      ...(mh > 0
        ? [
          {
            name: "Mother's Height",
            type: "line",
            data: heightList.map((h) => [
              new Date(h.chartLabel).getTime(),
              mh,
            ]),
            lineStyle: {
              width: 2,
              color: "#c084fc",
            },
            symbol: "none",
            z: 2,
            animation: false,//  Disable line animation
            markPoint: {
              symbolSize: 30,
              itemStyle: {
                color: "#c084fc",
              },
              data: [
                {
                  coord: [lastTimestamp, mh],
                  value: "M",
                  label: {
                    formatter: "M",
                    fontSize: 10,
                    fontWeight: "bold",
                    color: "#000",
                  },
                },
              ],
            },
          },
        ]
        : []),

      // Father's Height
      ...(fh > 0
        ? [
          {
            name: "Father's Height",
            type: "line",
            data: heightList.map((h) => [
              new Date(h.chartLabel).getTime(),
              fh,
            ]),
            lineStyle: {
              width: 2,
              color: "#60a5fa",
            },
            symbol: "none",
            z: 2,
            animation: false,//  Disable line animation
            markPoint: {
              symbolSize: 30,
              itemStyle: { color: "#60a5fa" },
              data: [
                {
                  coord: [lastTimestamp, fh],
                  value: "F",
                  label: {
                    formatter: "F",
                    fontSize: 10,
                    fontWeight: "bold",
                    color: "#000",
                  },
                },
              ],
            },
          },
        ]
        : []),
    ],
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
      }}
    >
      <ReactECharts
        option={HeightOption}
        style={{
          width: "100%",
          height: isDownload ? "750px" : isMobileScreen ? "70vh" : "85vh" // <-- mobile vs full screen
        }}
        notMerge={true}
        lazyUpdate={true}
        opts={{ renderer: 'canvas' }}
      />
    </div>
  );
};

export default React.memo(HeightChart);
